import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Paper,
  Select,
  MenuItem,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import axios from "axios";
import { useRecoilValue } from "recoil";
import { LoginUserAtom } from "../stores/LoginUserAtom";
import ConstList from "../utils/ConstList";

type Props = {
  form: any;
  setForm: React.Dispatch<React.SetStateAction<any>>;
  onSaved: () => void | Promise<void>;
  onCancel: () => void;

  salesPersonList: any[];
  companyList?: any[];
  approachList?: any[];
  leadList?: any[];
  serviceList?: any[];
};

export default function ProsManagementEdit({
  form,
  setForm,
  onSaved,
  onCancel,
  salesPersonList,
  companyList = [],
  approachList = [],
  leadList = [],
  serviceList = [],
}: Props) {
  const loginUserData = useRecoilValue(LoginUserAtom);
  const [saving, setSaving] = useState(false);

  // =================================================
  // 共通：通常項目変更
  // =================================================
  const handleChange = (key: string, value: any) => {
    setForm((prev: any) => ({
      ...prev,
      [key]: value,
    }));
  };

  // =================================================
  // 共通：Select変更
  // IDと表示名を両方formに入れる
  // =================================================
  const handleSelectChange = ({
    idKey,
    nameKey,
    value,
    list,
    listIdKey,
    listNameKey,
  }: {
    idKey: string;
    nameKey: string;
    value: string;
    list: any[];
    listIdKey: string;
    listNameKey: string;
  }) => {
    const selected = list.find((item) => String(item[listIdKey]) === value);

    setForm((prev: any) => ({
      ...prev,
      [idKey]: value === "" ? "" : Number(value),
      [nameKey]: selected?.[listNameKey] ?? "",
    }));
  };

  // =================================================
  // 共通：Selectの現在値取得
  // IDがあればIDを使う
  // IDがなければ表示名からIDを逆引きする
  // =================================================
  const getSelectIdValue = ({
    idValue,
    nameValue,
    list,
    listIdKey,
    listNameKey,
  }: {
    idValue: any;
    nameValue: any;
    list: any[];
    listIdKey: string;
    listNameKey: string;
  }) => {
    if (idValue !== undefined && idValue !== null && idValue !== "") {
      return String(idValue);
    }

    const found = list.find((item) => item[listNameKey] === nameValue);
    return found ? String(found[listIdKey]) : "";
  };

  // =================================================
  // 共通：Selectの表示名取得
  // renderValueで使う
  // クリックしていない状態でも表示を消さないための処理
  // =================================================
  const getSelectDisplayName = ({
    value,
    fallbackName,
    list,
    listIdKey,
    listNameKey,
  }: {
    value: any;
    fallbackName: any;
    list: any[];
    listIdKey: string;
    listNameKey: string;
  }) => {
    const found = list.find(
      (item) => String(item[listIdKey]) === String(value),
    );

    if (found) {
      return found[listNameKey];
    }

    if (fallbackName) {
      return fallbackName;
    }

    return "未選択";
  };

  // =================================================
  // 保存処理
  // =================================================
  const handleSave = async () => {
    if (!form) return;

    try {
      setSaving(true);

      const fixedForm = {
        ...form,

        company_id:
          form.company_id ||
          companyList.find((c: any) => c.company_name === form.Company_name)
            ?.company_id ||
          "",

        sales_person_id:
          form.sales_person_id ||
          salesPersonList.find((p: any) => p.sales_person === form.Sales_person)
            ?.sales_person_id ||
          "",

        sales_approach_method_id:
          form.sales_approach_method_id ||
          approachList.find(
            (a: any) => a.sales_approach_method === form.Sales_approach_method,
          )?.sales_approach_method_id ||
          "",

        lead_sources_id:
          form.lead_sources_id ||
          leadList.find((l: any) => l.lead_sources === form.Lead_sources)
            ?.lead_sources_id ||
          "",

        proposed_service_id:
          form.proposed_service_id ||
          serviceList.find(
            (s: any) => s.proposed_service === form.Service_details,
          )?.proposed_service_id ||
          "",
      };

      console.log("送信データ", fixedForm);

      const res = await axios.post(
        ConstList.getApiUrl() + "Edit_Pros_Management.php",
        {
          flag: "update_sales",
          data: fixedForm,
          access_token: loginUserData.accessToken,
          algorithm: loginUserData.algorithm,
        },
        ConstList.AXIOS_CONFIG,
      );

      console.log("更新結果", res.data);

      if (res.data?.status !== 1) {
        alert(res.data?.message || "更新に失敗しました");
        return;
      }

      alert("更新しました");

      await onSaved();
    } catch (error) {
      console.error(error);
      alert("更新処理でエラーが発生しました");
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="#64748B">編集データを読み込み中です。</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: 3,
        bgcolor: "#F1F5F9",
        minHeight: "100%",
      }}
    >
      {/* ===== ヘッダー ===== */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography fontSize={20} fontWeight={700} color="#0F172A">
            営業履歴編集
          </Typography>
          <Typography fontSize={12} color="#64748B">
            営業履歴ID：{form?.Sales_record_id || "未設定"}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            onClick={onCancel}
            sx={{ color: "#475569" }}
            disabled={saving}
          >
            キャンセル
          </Button>

          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? "保存中..." : "保存"}
          </Button>
        </Stack>
      </Stack>

      {/* ===== 基本情報 ===== */}
      <Paper sx={cardStyle}>
        <SectionTitle title="基本情報" />

        <Stack spacing={2.5}>
          {/* 会社名 */}
          <Field label="会社名">
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={getSelectIdValue({
                idValue: form?.company_id,
                nameValue: form?.Company_name,
                list: companyList,
                listIdKey: "company_id",
                listNameKey: "company_name",
              })}
              renderValue={(selected) =>
                getSelectDisplayName({
                  value: selected,
                  fallbackName: form?.Company_name,
                  list: companyList,
                  listIdKey: "company_id",
                  listNameKey: "company_name",
                })
              }
              onChange={(e) =>
                handleSelectChange({
                  idKey: "company_id",
                  nameKey: "Company_name",
                  value: String(e.target.value),
                  list: companyList,
                  listIdKey: "company_id",
                  listNameKey: "company_name",
                })
              }
              sx={inputStyle}
            >
              <MenuItem value="">未選択</MenuItem>

              {companyList.map((c: any) => (
                <MenuItem key={c.company_id} value={String(c.company_id)}>
                  {c.company_name}
                </MenuItem>
              ))}
            </Select>
          </Field>

          {/* 営業担当 */}
          <Field label="営業担当">
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={getSelectIdValue({
                idValue: form?.sales_person_id,
                nameValue: form?.Sales_person,
                list: salesPersonList,
                listIdKey: "sales_person_id",
                listNameKey: "sales_person",
              })}
              renderValue={(selected) =>
                getSelectDisplayName({
                  value: selected,
                  fallbackName: form?.Sales_person,
                  list: salesPersonList,
                  listIdKey: "sales_person_id",
                  listNameKey: "sales_person",
                })
              }
              onChange={(e) =>
                handleSelectChange({
                  idKey: "sales_person_id",
                  nameKey: "Sales_person",
                  value: String(e.target.value),
                  list: salesPersonList,
                  listIdKey: "sales_person_id",
                  listNameKey: "sales_person",
                })
              }
              sx={inputStyle}
            >
              <MenuItem value="">未選択</MenuItem>

              {salesPersonList.map((op: any) => (
                <MenuItem
                  key={op.sales_person_id}
                  value={String(op.sales_person_id)}
                >
                  {op.sales_person}
                </MenuItem>
              ))}
            </Select>
          </Field>

          {/* 接触日 */}
          <Field label="接触日">
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
              <DatePicker
                value={form?.contact_day ? dayjs(form.contact_day) : null}
                onChange={(v) =>
                  handleChange(
                    "contact_day",
                    v && v.isValid() ? v.format("YYYY-MM-DD") : "",
                  )
                }
                format="YYYY年MM月DD日"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: "small",
                    sx: inputStyle,
                  },
                }}
              />
            </LocalizationProvider>
          </Field>

          {/* 顧客担当者 */}
          <TextField
            label="顧客担当者"
            fullWidth
            size="small"
            value={form?.Contact_person || ""}
            onChange={(e) => handleChange("Contact_person", e.target.value)}
            sx={inputStyle}
          />
        </Stack>
      </Paper>

      {/* ===== 営業アクション ===== */}
      <Paper sx={cardStyle}>
        <SectionTitle title="営業アクション" />

        <Stack spacing={2.5}>
          {/* 営業方法 */}
          <Field label="営業方法">
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={getSelectIdValue({
                idValue: form?.sales_approach_method_id,
                nameValue: form?.Sales_approach_method,
                list: approachList,
                listIdKey: "sales_approach_method_id",
                listNameKey: "sales_approach_method",
              })}
              renderValue={(selected) =>
                getSelectDisplayName({
                  value: selected,
                  fallbackName: form?.Sales_approach_method,
                  list: approachList,
                  listIdKey: "sales_approach_method_id",
                  listNameKey: "sales_approach_method",
                })
              }
              onChange={(e) =>
                handleSelectChange({
                  idKey: "sales_approach_method_id",
                  nameKey: "Sales_approach_method",
                  value: String(e.target.value),
                  list: approachList,
                  listIdKey: "sales_approach_method_id",
                  listNameKey: "sales_approach_method",
                })
              }
              sx={inputStyle}
            >
              <MenuItem value="">未選択</MenuItem>

              {approachList.map((op: any) => (
                <MenuItem
                  key={op.sales_approach_method_id}
                  value={String(op.sales_approach_method_id)}
                >
                  {op.sales_approach_method}
                </MenuItem>
              ))}
            </Select>
          </Field>

          {/* きっかけ */}
          <Field label="きっかけ">
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={getSelectIdValue({
                idValue: form?.lead_sources_id,
                nameValue: form?.Lead_sources,
                list: leadList,
                listIdKey: "lead_sources_id",
                listNameKey: "lead_sources",
              })}
              renderValue={(selected) =>
                getSelectDisplayName({
                  value: selected,
                  fallbackName: form?.Lead_sources,
                  list: leadList,
                  listIdKey: "lead_sources_id",
                  listNameKey: "lead_sources",
                })
              }
              onChange={(e) =>
                handleSelectChange({
                  idKey: "lead_sources_id",
                  nameKey: "Lead_sources",
                  value: String(e.target.value),
                  list: leadList,
                  listIdKey: "lead_sources_id",
                  listNameKey: "lead_sources",
                })
              }
              sx={inputStyle}
            >
              <MenuItem value="">未選択</MenuItem>

              {leadList.map((op: any) => (
                <MenuItem
                  key={op.lead_sources_id}
                  value={String(op.lead_sources_id)}
                >
                  {op.lead_sources}
                </MenuItem>
              ))}
            </Select>
          </Field>
        </Stack>
      </Paper>

      {/* ===== 提案内容 ===== */}
      <Paper sx={cardStyle}>
        <SectionTitle title="提案内容" />

        <Stack spacing={2.5}>
          {/* 提案サービス */}
          <Field label="提案サービス">
            <Select
              fullWidth
              size="small"
              displayEmpty
              value={getSelectIdValue({
                idValue: form?.proposed_service_id,
                nameValue: form?.Service_details,
                list: serviceList,
                listIdKey: "proposed_service_id",
                listNameKey: "proposed_service",
              })}
              renderValue={(selected) =>
                getSelectDisplayName({
                  value: selected,
                  fallbackName: form?.Service_details,
                  list: serviceList,
                  listIdKey: "proposed_service_id",
                  listNameKey: "proposed_service",
                })
              }
              onChange={(e) =>
                handleSelectChange({
                  idKey: "proposed_service_id",
                  nameKey: "Service_details",
                  value: String(e.target.value),
                  list: serviceList,
                  listIdKey: "proposed_service_id",
                  listNameKey: "proposed_service",
                })
              }
              sx={inputStyle}
            >
              <MenuItem value="">未選択</MenuItem>

              {serviceList.map((op: any) => (
                <MenuItem
                  key={op.proposed_service_id}
                  value={String(op.proposed_service_id)}
                >
                  {op.proposed_service}
                </MenuItem>
              ))}
            </Select>
          </Field>

          {/* 提案詳細 */}
          <TextField
            label="提案詳細"
            fullWidth
            multiline
            rows={4}
            value={form?.Proposal_details || ""}
            onChange={(e) => handleChange("Proposal_details", e.target.value)}
            sx={inputStyle}
          />
        </Stack>
      </Paper>

      {/* ===== 振り返り ===== */}
      <Paper sx={cardStyle}>
        <SectionTitle title="振り返り" />

        <TextField
          label="営業レビュー"
          fullWidth
          multiline
          rows={4}
          value={form?.Sales_review || ""}
          onChange={(e) => handleChange("Sales_review", e.target.value)}
          sx={inputStyle}
        />
      </Paper>
    </Box>
  );
}

/* ===== スタイル ===== */

const cardStyle = {
  p: 3,
  mb: 3,
  borderRadius: 3,
  background: "#F8FAFC",
  border: "1px solid #CBD5E1",
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
};

const inputStyle = {
  backgroundColor: "#fff",
  borderRadius: 2,

  // TextField / DatePicker / Select 共通
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    borderRadius: 2,
    color: "#111827",

    "& fieldset": {
      borderColor: "#CBD5E1",
    },

    "&:hover": {
      backgroundColor: "#fff",
    },

    "&:hover fieldset": {
      borderColor: "#94A3B8",
    },

    "&.Mui-focused": {
      backgroundColor: "#fff",
    },

    "&.Mui-focused fieldset": {
      borderColor: "#6366F1",
      borderWidth: "1px",
    },
  },

  // 入力文字
  "& .MuiInputBase-input": {
    color: "#111827",
    fontWeight: 500,
    backgroundColor: "#fff",
  },

  // Selectの表示部分
  "& .MuiSelect-select": {
    color: "#111827",
    fontWeight: 500,
    backgroundColor: "#fff !important",
  },

  // ラベル
  "& .MuiInputLabel-root": {
    color: "#64748B",
  },

  "& .MuiInputLabel-root.Mui-focused": {
    color: "#6366F1",
  },
};

/* ===== 共通ラベル ===== */

const Field = ({ label, children }: any) => (
  <Box>
    <Typography fontSize={12} color="#64748B" mb={0.5}>
      {label}
    </Typography>
    {children}
  </Box>
);

const SectionTitle = ({ title }: { title: string }) => (
  <Typography
    fontWeight={700}
    mb={2}
    color="#1E293B"
    sx={{
      borderLeft: "4px solid #6366F1",
      pl: 1,
    }}
  >
    {title}
  </Typography>
);
